import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Button,
    IconButton,
    Select,
    MenuItem,
    Checkbox,
    Typography,
    Tooltip,
    LinearProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";

import {
    uploadLanguageFile,
    importLanguageData,
    updateLanguageSelection,
    setInitialSelection,
    downloadTemplate,
    setFileFormat,
    removeUploadedFile,
    resetLanguageDialogState,
    toggleDownloadSection,
    cancelUpload,
    showConfirmationDialog,
    confirmLanguageRemoval,
    cancelLanguageRemoval,
} from "../../Services/redux/AddLanguageDialog/AddLanguageDialogActions";
import { SHOW_CONFIRMATION_DIALOG } from "../../Services/redux/AddLanguageDialog/AddLanguageDialogTypes";

const getLanguageNames = (languageTags, languageData) => {
    return languageTags.map(tag => {
        const lang = languageData?.find(l => l.languageTag === tag);
        return lang ? `${lang.languageTag}` : tag;
    }).join(', ');
};

const LanguageMenuItem = ({ lang, submissionData, selectedLanguages, handleCheckBoxChange }) => {
    const isDefaultLanguage = lang.languageTag === submissionData.defaultLanguage;
    
    return (
        <MenuItem
            key={lang.languageTag}
            value={lang.languageTag}
            disabled={isDefaultLanguage}
            onClick={(e) => e.stopPropagation()}
        >
            <Checkbox
                checked={selectedLanguages.includes(lang.languageTag) || isDefaultLanguage}
                disabled={isDefaultLanguage}
                onChange={(e) => handleCheckBoxChange(e, lang.languageTag)}
                onClick={(e) => e.stopPropagation()}
            />
            {lang.defaultName} ({lang.defaultCountry}) - {lang.languageTag}
        </MenuItem>
    );
};

LanguageMenuItem.propTypes = {
    lang: PropTypes.shape({
        languageTag: PropTypes.string.isRequired,
        defaultName: PropTypes.string.isRequired,
        defaultCountry: PropTypes.string.isRequired,
    }).isRequired,
    submissionData: PropTypes.shape({
        defaultLanguage: PropTypes.string,
    }).isRequired,
    selectedLanguages: PropTypes.array.isRequired,
    handleCheckBoxChange: PropTypes.func.isRequired,
};

const DownloadTemplateSection = ({ fileFormat, handleFileFormatChange, handleDownload }) => (
    <div
        style={{
            marginTop: "24px",
            padding: "16px",
            backgroundColor: "#F3F3F3",
            borderRadius: "8px",
        }}
    >
        <Typography
            sx={{
                fontSize: "12px",
                fontWeight: 500,
                marginBottom: "12px",
                color: "#414344",
                lineHeight: "16px",
                letterSpacing: "0.12px"
            }}
        >
            Select your preferred file format and click download
        </Typography>
        <RadioGroup
            value={fileFormat}
            onChange={handleFileFormatChange}
            sx={{
                marginBottom: "12px",
                flexDirection: "row",
                gap: "16px",
            }}
        >
            <FormControlLabel
                value="Excel"
                control={<Radio />}
                label="Excel"
                sx={{
                    margin: 0,
                    border: fileFormat === "Excel" ? "2px solid #0476E2" : "1px solid #CCCCCC",
                    borderRadius: "4px",
                    padding: "4px 8px 4px 0px",
                    width: "77px",
                    height: "28px",
                    backgroundColor: fileFormat === "Excel" ? "#E5F3FF" : "transparent",
                    "&:hover": {
                        borderColor: "#121415",
                    },
                    "& .MuiFormControlLabel-label": {
                        fontSize: "14px",
                        color: "#121415",
                        lineHeight: "20px",
                        letterSpacing: "0.25px",
                    },
                }}
            />
            <FormControlLabel
                value="JSON"
                control={<Radio />}
                label="JSON"
                sx={{
                    margin: 0,
                    border: fileFormat === "JSON" ? "2px solid #0476E2" : "1px solid #CCCCCC",
                    borderRadius: "4px",
                    padding: "4px 8px 4px 0px",
                    width: "77px",
                    height: "28px",
                    backgroundColor: fileFormat === "JSON" ? "#E5F3FF" : "transparent",
                    "&:hover": {
                        borderColor: "#121415",
                    },
                    "& .MuiFormControlLabel-label": {
                        fontSize: "14px",
                        color: "#121415",
                        lineHeight: "20px",
                        letterSpacing: "0.25px",
                    },
                }}
            />
        </RadioGroup>
        <Button
            variant="contained"
            fullWidth
            onClick={handleDownload}
            disabled={!fileFormat}
            sx={{
                height: "28px",
                width: "max-content",
                textTransform: "none",
                backgroundColor: fileFormat ? "#0476E2" : "#C2DFFB",
                color: "#FFFFFF",
                fontSize: "14px",
                fontWeight: 500,
                padding: "5px 10px",
                borderRadius: "4px",
                boxShadow: "none",
                "&:hover": {
                    backgroundColor: fileFormat ? "#0476E2" : "#C2DFFB",
                    boxShadow: "none",
                },
                "&.Mui-disabled": {
                    backgroundColor: "#C2DFFB",
                    color: "#FFFFFF",
                },
            }}
        >
            Download Template
        </Button>
    </div>
);

DownloadTemplateSection.propTypes = {
    fileFormat: PropTypes.string,
    handleFileFormatChange: PropTypes.func.isRequired,
    handleDownload: PropTypes.func.isRequired,
};

const UpdateManuallySection = ({ handleUpdateManually }) => (
    <div
        style={{
            marginTop: "24px",
            padding: "16px",
            backgroundColor: "#F3F3F3",
            borderRadius: "8px",
        }}
    >
        <Typography
            sx={{
                fontSize: "12px",
                fontWeight: 500,
                marginBottom: "12px",
                color: "#414344",
                lineHeight: "16px",
                letterSpacing: "0.12px"
            }}
        >
            One line description goes here
        </Typography>
        <Button
            variant="outlined"
            fullWidth
            onClick={handleUpdateManually}
            sx={{
                height: "28px",
                width: "max-content",
                textTransform: "none",
                borderColor: "#0476E2",
                color: "#0476E2",
                fontSize: "14px",
                fontWeight: 500,
                padding: "5px 10px",
                borderRadius: "4px",
            }}
        >
            Update Manually
        </Button>
    </div>
);

UpdateManuallySection.propTypes = {
    handleUpdateManually: PropTypes.func.isRequired,
};

const ConfirmationDialog = ({
    open,
    onClose,
    onConfirm,
    errorMessage,
    languagesToRemove,
    getLanguageDisplayNames
}) => {
    const showButtons = !errorMessage || errorMessage.startsWith("Are you sure you want to remove");
    const displayMessage = errorMessage || 
        `Are you sure you want to remove ${getLanguageDisplayNames(languagesToRemove || [])} ${languagesToRemove?.length > 1 ? 'languages' : 'language'}?`;
    
    return (
        <Dialog
            open={open}
            onClose={onClose}
            aria-labelledby="confirm-dialog-title"
            slotProps={{
                paper: {
                    sx: {
                        borderRadius: "8px",
                        padding: "16px",
                        width: "469px",
                        background: "linear-gradient(90deg, #FBC2BC 0%, #F89A92 100%)",
                    },
                },
            }}
        >
            <DialogTitle
                id="confirm-dialog-title"
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "0 0 16px 0",
                }}
            >
                <Typography
                    sx={{
                        fontSize: "16px",
                        fontWeight: 600,
                        color: "#121415",
                        lineHeight: "20px",
                        letterSpacing: "0.25px",
                    }}
                >
                    Error
                </Typography>
                <IconButton
                    onClick={onClose}
                    size="small"
                    sx={{
                        color: "#121415",
                        padding: "4px",
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ padding: "0 0 0 0" }}>
                <Typography
                    sx={{
                        fontSize: "14px",
                        fontWeight: 400,
                        color: "#121415",
                        marginBottom: "8px",
                        lineHeight: "20px",
                        letterSpacing: "0.25px",
                    }}
                >
                    {displayMessage}
                </Typography>
                {showButtons && (
                    <div style={{ display: "flex", gap: "8px" }}>
                        <Button
                            variant="contained"
                            onClick={onConfirm}
                            sx={{
                                width: "107px",
                                textTransform: "none",
                                backgroundColor: "#121415",
                                color: "#FFFFFF",
                                fontSize: "12px",
                                fontWeight: 500,
                                padding: "5px 10px",
                                borderRadius: "4px",
                                boxShadow: "none",
                                "&:hover": {
                                    backgroundColor: "#121415",
                                    boxShadow: "none",
                                },
                            }}
                        >
                            Yes, Continue
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={onClose}
                            sx={{
                                width: "107px",
                                textTransform: "none",
                                backgroundColor: "transparent",
                                color: "#121415",
                                borderColor: "#121415",
                                fontSize: "12px",
                                fontWeight: 500,
                                padding: "5px 10px",
                                borderRadius: "4px",
                                "&:hover": {
                                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                                    borderColor: "#121415",
                                },
                            }}
                        >
                            No, Cancel
                        </Button>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};

ConfirmationDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onConfirm: PropTypes.func.isRequired,
    errorMessage: PropTypes.string,
    languagesToRemove: PropTypes.array,
    getLanguageDisplayNames: PropTypes.func.isRequired,
};

const processFileUpload = (file, dispatch, submissionData, languageData, selectedLanguages, onInvalidFormat) => {
    const fileExtension = file.name.split('.').pop().toLowerCase();
    const allowedFormats = ['json', 'xlsx', 'xls'];

    if (!allowedFormats.includes(fileExtension)) {
        dispatch({
            type: SHOW_CONFIRMATION_DIALOG,
            payload: {
                languagesToRemove: [],
                pendingSelection: [],
                errorMessage: "JSON Metadata file is not proper format, please recheck the file and upload!"
            }
        });
        onInvalidFormat?.();
        return;
    }

    dispatch(uploadLanguageFile(file, submissionData.defaultLanguage, selectedLanguages, languageData, (missingLanguages, availableLanguages) => {
        if (missingLanguages?.length > 0) {
            dispatch(showConfirmationDialog(missingLanguages, availableLanguages));
        }
    }));
};

const dispatchFileImport = (dispatch, payload, data, onImportData, onComplete) => {
    const completePayload = { ...payload };
    const apiCallback = async (finalPayload, legalDocuments, finalLanguages) => {
        if (onImportData && typeof onImportData === 'function') {
            await onImportData(finalPayload, legalDocuments, finalLanguages);
        }
        onComplete?.();
    };
    dispatch(importLanguageData(completePayload, data, apiCallback));
};

const handleCheckBoxChangeUtil = (event, languageTag, selectedLanguages, dispatch, onLanguageChange) => {
    event.stopPropagation();
    const currentlySelected = [...selectedLanguages];
    let newSelectedOptions;
    if (event.target.checked) {
        newSelectedOptions = currentlySelected.includes(languageTag)
            ? currentlySelected
            : [...currentlySelected, languageTag];
    } else {
        newSelectedOptions = currentlySelected.filter((item) => item !== languageTag);
    }
    dispatch(updateLanguageSelection(newSelectedOptions));
    onLanguageChange?.(newSelectedOptions);
};

const handleSelectChangeUtil = (e, submissionData, dispatch, onLanguageChange) => {
    const value = e.target.value;
    const newSelection = typeof value === 'string' ? value.split(',') : value;
    const nonDefaultSelection = newSelection.filter(lang => lang !== submissionData.defaultLanguage);
    dispatch(updateLanguageSelection(nonDefaultSelection));
    onLanguageChange?.(nonDefaultSelection);
};

const handleCancelRemovalUtil = (uploadedFile, dispatch) => {
    dispatch(cancelLanguageRemoval());
    if (uploadedFile) {
        dispatch(removeUploadedFile());
    }
    const fileInput = document.getElementById('file-upload-input');
    if (fileInput) {
        fileInput.value = '';
    }
};

const checkRemovedLanguages = (initialLanguages, selectedLanguages) => {
    const currentInitial = initialLanguages || [];
    const currentSelected = selectedLanguages || [];
    return currentInitial.filter(lang => !currentSelected.includes(lang));
};

const renderSelectedLanguages = (selected) => {
    const totalCount = 1 + selected.length;
    return (
        <span style={{
            color: "#121415",
            fontSize: "14px",
            fontWeight: 400,
            lineHeight: "20px",
        }}>
            {totalCount} language{totalCount > 1 ? "s" : ""} selected
        </span>
    );
};


const UploadedFileDisplay = ({ uploadedFile, handleRemoveFile }) => (
    <>
        <Typography
            sx={{
                fontSize: "14px",
                fontWeight: 600,
                color: "#000000",
                marginBottom: "16px",
                lineHeight: "16px",
                letterSpacing: "0px",
            }}
        >
            Uploaded file
        </Typography>
        <div
            style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 12px",
                backgroundColor: "#C2DFFB",
                borderRadius: "4px",
            }}
        >
            <Typography
                sx={{
                    fontSize: "14px",
                    color: "#000000",
                    fontWeight: 400,
                    lineHeight: "20px",
                    letterSpacing: "0.25px",
                }}
            >
                {uploadedFile.name}
            </Typography>
            <IconButton
                size="small"
                onClick={handleRemoveFile}
                sx={{ padding: "4px" }}
            >
                <DeleteOutlineIcon sx={{ fontSize: "20px", color: "#121415" }} />
            </IconButton>
        </div>
    </>
);

UploadedFileDisplay.propTypes = {
    uploadedFile: PropTypes.shape({
        name: PropTypes.string.isRequired,
    }).isRequired,
    handleRemoveFile: PropTypes.func.isRequired,
};

const UploadingProgress = ({ uploadedFile, uploadProgress, handleCancelUpload }) => (
    <>
        <Typography
            sx={{
                fontSize: "14px",
                fontWeight: 600,
                color: "#121415",
                marginBottom: "24px",
                lineHeight: "20px",
                letterSpacing: "0.25px",
            }}
        >
            File name:{" "}
            <span style={{ fontWeight: 400 }}>
                {uploadedFile.name}
            </span>
        </Typography>
        <div style={{ marginTop: "16px" }}>
            <LinearProgress
                variant="determinate"
                value={uploadProgress}
                sx={{
                    height: "8px",
                    borderRadius: "4px",
                    backgroundColor: "#E0E0E0",
                    "& .MuiLinearProgress-bar": {
                        backgroundColor: "#0476E2",
                        borderRadius: "4px",
                    },
                }}
            />
            <Typography
                sx={{
                    fontSize: "14px",
                    color: "#121415",
                    fontWeight: 600,
                    marginBottom: "26px",
                    textAlign: "left",
                    lineHeight: "20px",
                    letterSpacing: "0.25px",
                }}
            >
                Uploading...{" "}
                <span style={{ float: "right", fontWeight: 600 }}>{uploadProgress}%</span>
            </Typography>
            <div style={{ display: "flex", justifyContent: "center" }}>
                <Button
                    variant="outlined"
                    size="small"
                    onClick={handleCancelUpload}
                    sx={{
                        fontWeight: 500,
                        width: "98px",
                        height: "24px",
                        textTransform: "none",
                        fontSize: "12px",
                        padding: "5px 10px",
                        borderColor: "#CCCCCC",
                        color: "#2A2C2D",
                        borderRadius: "4px",
                        whiteSpace: "nowrap",
                        "&:hover": {
                            borderColor: "#121415",
                        },
                    }}
                >
                    Cancel Upload
                </Button>
            </div>
        </div>
    </>
);

UploadingProgress.propTypes = {
    uploadedFile: PropTypes.shape({
        name: PropTypes.string.isRequired,
    }).isRequired,
    uploadProgress: PropTypes.number.isRequired,
    handleCancelUpload: PropTypes.func.isRequired,
};

function AddLanguageDialog({
    open,
    onClose,
    languageData,
    submissionData,
    selectedOptions,
    onUpdateManually,
    onImportData,
    onLanguageChange,
}) {
    const dispatch = useDispatch();
    const {
        uploadedFile,
        uploadProgress,
        isUploading,
        parsedData,
        fileFormat,
        showDownloadSection,
        selectedLanguages,
        initialSelectedLanguages,
        importSuccess,
        showConfirmationDialog: confirmDialogOpen,
        languagesToRemove,
        pendingSelection,
        errorMessage,
    } = useSelector(state => state.addLanguageDialog);

    const runFileImport = (payload, data, onComplete) => {
        dispatchFileImport(dispatch, payload, data, onImportData, onComplete);
    };

    useEffect(() => {
        if (open && submissionData) {
            const nonDefaultLanguages = submissionData.descriptions
                ?.map(desc => desc.language)
                .filter(lang => lang && lang !== submissionData.defaultLanguage) || [];

            dispatch(setInitialSelection(nonDefaultLanguages));
        }
    }, [open, submissionData, dispatch]);

    useEffect(() => {
        if (!importSuccess) return;
        
        dispatch(resetLanguageDialogState());
        onClose();
    }, [importSuccess, dispatch, onClose]);

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (!file) return;
        
        processFileUpload(file, dispatch, submissionData, languageData, selectedLanguages, () => {
            event.target.value = '';
        });
    };

    const handleDragOver = (event) => {
        event.preventDefault();
    };

    const handleDrop = (event) => {
        event.preventDefault();
        const file = event.dataTransfer.files[0];
        if (!file) return;
        
        processFileUpload(file, dispatch, submissionData, languageData, selectedLanguages);
    };

    const handleRemoveFile = () => {
        dispatch(removeUploadedFile());
    };

    const handleCancelUpload = () => {
        dispatch(cancelUpload());
    };

    const handleCheckBoxChange = (event, languageTag) =>
        handleCheckBoxChangeUtil(event, languageTag, selectedLanguages, dispatch, onLanguageChange);

    const handleImportData = () => {
        const isValidData = uploadedFile && parsedData && Array.isArray(parsedData) && parsedData.length > 0;
        if (!isValidData) {
            console.error("Invalid file data");
            return;
        }

        const removedLanguages = checkRemovedLanguages(initialSelectedLanguages, selectedLanguages);
        if (removedLanguages.length > 0) {
            dispatch(showConfirmationDialog(removedLanguages, selectedLanguages));
        } else {
            runFileImport(submissionData, parsedData, () => onLanguageChange?.(selectedLanguages));
        }
    };

    const handleDownloadTemplate = () => {
        dispatch(toggleDownloadSection(true));
    };

    const handleFileFormatChange = (event) => {
        dispatch(setFileFormat(event.target.value));
    };

    const handleDownload = () => {
        dispatch(downloadTemplate(fileFormat, languageData, submissionData, selectedLanguages));
    };

    const handleClose = () => {
        dispatch(resetLanguageDialogState());
        onClose();
    };

    const handleUpdateManually = () => {
        const removedLanguages = checkRemovedLanguages(initialSelectedLanguages, selectedLanguages);
        const hasRemovedLanguages = removedLanguages.length > 0;
        
        if (hasRemovedLanguages) {
            dispatch(showConfirmationDialog(removedLanguages, selectedLanguages));
        } else {
            onLanguageChange?.(selectedLanguages);
            onUpdateManually?.();
            handleClose();
        }
    };

    const handleSelectChange = (e) =>
        handleSelectChangeUtil(e, submissionData, dispatch, onLanguageChange);

    const handleConfirmRemoval = () => {
        dispatch(confirmLanguageRemoval());

        const hasUploadedData = uploadedFile && parsedData;
        if (hasUploadedData) {
            runFileImport(submissionData, parsedData, () => onLanguageChange?.(pendingSelection));
        } else {
            onLanguageChange?.(pendingSelection);
            setTimeout(() => {
                onUpdateManually?.();
                handleClose();
            }, 100);
        }
    };

    const handleCancelRemoval = () => handleCancelRemovalUtil(uploadedFile, dispatch);

    const getLanguageDisplayNames = (languageTags) => getLanguageNames(languageTags, languageData);

    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
                aria-labelledby="add-language-dialog-title"
                maxWidth="md"
                fullWidth
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: "8px",
                            padding: "0px",
                            width: "795px",
                            margin: 0,
                        },
                    },
                }}
            >
                <DialogTitle
                    id="add-language-dialog-title"
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "27px 24px",
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: "22px",
                            fontWeight: 600,
                            color: "#121415",
                        }}
                    >
                        Add language specific metadata
                    </Typography>
                    <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                        <IconButton onClick={handleClose} size="small"
                            sx={{
                                width: "32px",
                                height: "32px",
                                color: "#000000",
                                padding: "4px 6px 4px 6px",
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </div>
                </DialogTitle>
                <DialogContent
                    sx={{
                        padding: "24px",
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "24px",
                    }}
                >
                    {/* Left Section */}
                    <div
                        style={{
                            borderRight: "1px solid #CCCCCC",
                            paddingRight: "24px",
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: "18px",
                                fontWeight: 600,
                                marginBottom: "24px",
                                color: "#121415",
                            }}
                        >
                            Select Language
                        </Typography>
                        <Select
                            MenuProps={{
                                PaperProps: {
                                    style: {
                                        maxHeight: 300,
                                        overflow: "auto",
                                    },
                                },
                            }}
                            sx={{
                                width: "100%",
                                fontSize: "14px",
                                borderRadius: "6px",
                                "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: selectedLanguages.length > 0 ? "#C2DFFB" : "#CCCCCC",
                                    borderWidth: "1px",
                                },
                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#121415",
                                },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#C2DFFB",
                                    borderWidth: "2px",
                                },
                                "& .MuiSelect-select": {
                                    display: "flex",
                                    alignItems: "center",
                                    height: "0px !important",
                                    padding: "4px 12px !important",
                                    borderRadius: "4px",
                                },
                                "& .MuiSelect-select > span": {
                                    backgroundColor: "#E5F3FF",
                                    borderRadius: "4px",
                                    padding: "10px 14px",
                                    display: "block",
                                    width: "161px",
                                },
                            }}
                            multiple
                            displayEmpty
                            value={selectedLanguages}
                            onChange={handleSelectChange}
                            variant="outlined"
                            renderValue={renderSelectedLanguages}
                        >
                            {languageData?.map(lang => (
                                <LanguageMenuItem
                                    key={lang.languageTag}
                                    lang={lang}
                                    submissionData={submissionData}
                                    selectedLanguages={selectedLanguages}
                                    handleCheckBoxChange={handleCheckBoxChange}
                                />
                            ))}
                        </Select>

                        {/* Buttons */}
                        {!showDownloadSection && (
                            <div style={{ marginTop: "24px", display: "flex", gap: "12px" }}>
                                <Tooltip title="Manually update the Metadata to the selected languages">
                                    <Button
                                        variant="outlined"
                                        fullWidth
                                        onClick={handleUpdateManually}
                                        sx={{
                                            height: "36px",
                                            textTransform: "none",
                                            borderColor: "#0476E2",
                                            color: "#0476E2",
                                            fontSize: "14px",
                                            fontWeight: 500,
                                            padding: "10px 14px 10px 14px",
                                            borderRadius: "4px",
                                        }}
                                    >
                                        Update Manually
                                    </Button>
                                </Tooltip>
                                <Tooltip title="Update the template and upload to import data from the selected file format">
                                    <Button
                                        variant="contained"
                                        fullWidth
                                        onClick={handleDownloadTemplate}
                                        sx={{
                                            height: "36px",
                                            textTransform: "none",
                                            borderColor: "#0476E2",
                                            color: "#FFFFFF",
                                            fontSize: "14px",
                                            fontWeight: 500,
                                            padding: "10px 14px 10px 14px",
                                            borderRadius: "4px",
                                        }}
                                    >
                                        Download Template
                                    </Button>
                                </Tooltip>
                            </div>
                        )}

                        {/* Download Template Section */}
                        {showDownloadSection && (
                            <DownloadTemplateSection
                                fileFormat={fileFormat}
                                handleFileFormatChange={handleFileFormatChange}
                                handleDownload={handleDownload}
                            />
                        )}

                        {/* Update Manually Section */}
                        {showDownloadSection && (
                            <UpdateManuallySection handleUpdateManually={handleUpdateManually} />
                        )}
                    </div>

                    {/* Right Section - Upload Area */}
                    <div>
                        <Typography
                            sx={{
                                fontSize: "18px",
                                fontWeight: 600,
                                marginBottom: "4px",
                                color: "#121415",
                            }}
                        >
                            Upload JSON / Excel file
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: "14px",
                                color: "#5A5B5C",
                                marginBottom: "24px",
                            }}
                        >
                            Upload the updated template, and all data will be imported with a single click.
                        </Typography>

                        {/* Upload Box */}
                        {!uploadedFile && !isUploading ? (
                            <button
                                type="button"
                                onDragOver={handleDragOver}
                                onDrop={handleDrop}
                                onClick={() => document.getElementById("file-upload-input").click()}
                                style={{
                                    border: "2px dashed #CCCCCC",
                                    borderRadius: "4px",
                                    padding: "32px 45px",
                                    textAlign: "center",
                                    backgroundColor: "#F3F3F3",
                                    minHeight: "219px",
                                    width: "100%",
                                    maxWidth: "100%",
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: "pointer",
                                    transition: "all 0.3s ease",
                                }}
                            >
                                <input
                                    id="file-upload-input"
                                    type="file"
                                    accept="*"
                                    onChange={handleFileSelect}
                                    style={{ display: "none" }}
                                />
                                <div
                                    style={{
                                        width: "72px",
                                        height: "72px",
                                        backgroundColor: "#E8E8E8",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        marginBottom: "24px",
                                        borderRadius: "50%",
                                    }}
                                >
                                    <div
                                        style={{
                                            width: "48px",
                                            height: "48px",
                                            borderRadius: "50%",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            border: "1px solid #CCCCCC",
                                            backgroundColor: "#FFFFFF",
                                        }}
                                    >
                                        <FileUploadOutlinedIcon
                                            sx={{ fontSize: "24px", color: "#414344", width: "16px", height: "16px" }}
                                        />
                                    </div>
                                </div>
                                <Typography
                                    sx={{
                                        fontSize: "14px",
                                        color: "#121415",
                                        marginBottom: "8px",
                                        height: "16px"
                                    }}
                                >
                                    Drop the template file here, or{" "}
                                    <span
                                        style={{
                                            color: "#0066CC",
                                            cursor: "pointer",
                                            fontWeight: 500,
                                        }}
                                    >
                                        Browse
                                    </span>
                                </Typography>
                            </button>
                        ) : (
                            <div
                                style={{
                                    border: "1px dashed #CCCCCC",
                                    borderRadius: "8px",
                                    padding: "24px",
                                    backgroundColor: "#F0F0FF",
                                    minHeight: "219px",
                                }}
                            >
                                {isUploading ? (
                                    <UploadingProgress 
                                        uploadedFile={uploadedFile}
                                        uploadProgress={uploadProgress}
                                        handleCancelUpload={handleCancelUpload}
                                    />
                                ) : (
                                    <UploadedFileDisplay 
                                        uploadedFile={uploadedFile}
                                        handleRemoveFile={handleRemoveFile}
                                    />
                                )}
                            </div>
                        )}

                        {/* Bottom Buttons */}
                        <div
                            style={{
                                marginTop: "28px",
                                display: "flex",
                                gap: "12px",
                                justifyContent: "flex-end",
                            }}
                        >
                            <Button
                                variant="outlined"
                                onClick={handleClose}
                                sx={{
                                    textTransform: "none",
                                    color: "#121415",
                                    borderColor: "#CCCCCC",
                                    backgroundColor: "#E8E8E8",
                                    fontSize: "14px",
                                    fontWeight: 500,
                                    padding: "10px 35px",
                                    borderRadius: "4px",
                                    height: "36px",
                                    cursor: "pointer",
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleImportData}
                                disabled={!uploadedFile || isUploading || !parsedData}
                                sx={{
                                    textTransform: "none",
                                    color: "#FFFFFF",
                                    backgroundColor: (uploadedFile && !isUploading && parsedData) ? "#0476E2" : "#C2DFFB",
                                    fontSize: "14px",
                                    fontWeight: 500,
                                    padding: "8px 24px",
                                    borderRadius: "6px",
                                    height: "36px",
                                    cursor: (uploadedFile && !isUploading && parsedData) ? "pointer" : "not-allowed",
                                    boxShadow: "none",
                                    "&:hover": {
                                        backgroundColor: (uploadedFile && !isUploading && parsedData) ? "#0365C5" : "#C2DFFB",
                                        boxShadow: "none",
                                    },
                                    "&.Mui-disabled": {
                                        backgroundColor: "#C2DFFB",
                                        color: "#FFFFFF",
                                        cursor: "not-allowed",
                                    },
                                }}
                            >
                                Import Data
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            {/* Confirmation Dialog */}
            <ConfirmationDialog
                open={confirmDialogOpen}
                onClose={handleCancelRemoval}
                onConfirm={handleConfirmRemoval}
                errorMessage={errorMessage}
                languagesToRemove={languagesToRemove}
                getLanguageDisplayNames={getLanguageDisplayNames}
            />
        </>
    );
}

AddLanguageDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    languageData: PropTypes.array,
    submissionData: PropTypes.shape({
        defaultLanguage: PropTypes.string,
        descriptions: PropTypes.arrayOf(PropTypes.shape({
            language: PropTypes.string,
        })),
    }),
    selectedOptions: PropTypes.array,
    onUpdateManually: PropTypes.func,
    onImportData: PropTypes.func,
    onLanguageChange: PropTypes.func,
};

export default AddLanguageDialog;